import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "../utils/axios-instance";
import { ServerRoutes, TelegramBotUsername } from "../routes";
import { copyToClipboard } from "../hooks/useSnackbar";

const MAIN_CAMPAIGN = "main_channel";
// Telegram rejects startapp values longer than 64 characters.
const MAX_START_PARAM = 64;
const NAME_RE = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
const CODE_RE = /^[A-Za-z0-9-]+$/;
const LAST_CODE_KEY = "amoozical.campaignReferralCode";

export const buildCampaignLink = (referralCode: string, name: string) =>
  `https://t.me/${TelegramBotUsername}/start?startapp=${encodeURIComponent(
    `${referralCode}_${name}`,
  )}`;

type CreateCampaignLinkResponse = {
  campaign: { id: number; name: string; createdAt: string };
  created: boolean;
  referrer: { id: number; username: string; firstName: string };
  startParam: string;
};

const readLastCode = () => {
  try {
    return localStorage.getItem(LAST_CODE_KEY) ?? "";
  } catch {
    return "";
  }
};

const apiErrorMessage = (error: any) => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(" · ");
  return message || error?.message || "Failed to create campaign link";
};

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (campaignId: number) => void;
};

export default function CreateCampaignLinkDialog({
  open,
  onClose,
  onCreated,
}: Props) {
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState(readLastCode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateCampaignLinkResponse | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  const trimmedName = name.trim();
  const trimmedCode = referralCode.trim();
  const startParamLength = trimmedCode.length + 1 + trimmedName.length;

  const nameError = !trimmedName
    ? null
    : trimmedName === MAIN_CAMPAIGN
      ? `"${MAIN_CAMPAIGN}" is the default campaign`
      : !NAME_RE.test(trimmedName)
        ? "Letters, digits, _ and - only; start with a letter or digit"
        : null;
  const codeError =
    trimmedCode && !CODE_RE.test(trimmedCode)
      ? "Letters, digits and - only"
      : null;
  const tooLong = startParamLength > MAX_START_PARAM;
  const canSubmit =
    !!trimmedName && !!trimmedCode && !nameError && !codeError && !tooLong;

  const previewLink =
    trimmedName && trimmedCode
      ? buildCampaignLink(trimmedCode, trimmedName)
      : "";
  const resultLink = result
    ? `https://t.me/${TelegramBotUsername}/start?startapp=${encodeURIComponent(
        result.startParam,
      )}`
    : "";

  const reset = () => {
    setName("");
    setError(null);
    setResult(null);
    setCopied(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await axios.post<CreateCampaignLinkResponse>(
        ServerRoutes.campaigns,
        { name: trimmedName, referralCode: trimmedCode },
      );
      setResult(response.data);
      try {
        localStorage.setItem(LAST_CODE_KEY, trimmedCode);
      } catch {
        // Remembering the code is only a convenience.
      }
      onCreated(response.data.campaign.id);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (value: string) => {
    setCopied(await copyToClipboard(value));
  };

  const referrerLabel = result
    ? result.referrer.username
      ? `@${result.referrer.username}`
      : result.referrer.firstName || `User ${result.referrer.id}`
    : "";

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>New campaign link</DialogTitle>
      <DialogContent>
        {result ? (
          <Stack gap={2} mt={1}>
            <Alert severity="success">
              {result.created
                ? `Campaign "${result.campaign.name}" created.`
                : `Campaign "${result.campaign.name}" already existed — signups from this link are added to it.`}{" "}
              New users are attached to {referrerLabel} (#{result.referrer.id}
              ).
            </Alert>
            <TextField
              label="Campaign link"
              value={resultLink}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? "Copied" : "Copy link"}>
                      <IconButton onClick={() => handleCopy(resultLink)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Only users who sign up for the first time through this link are
              counted. Existing users keep their original campaign.
            </Typography>
          </Stack>
        ) : (
          <Stack gap={2.5} mt={1}>
            <TextField
              label="Campaign name"
              placeholder="instagram_launch"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!nameError}
              helperText={
                nameError ||
                "Shown in the stats. Letters, digits, _ and - (e.g. insta_nov, youtube-ad1)"
              }
              autoFocus
              fullWidth
            />
            <TextField
              label="Referral code"
              placeholder="799674"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              error={!!codeError}
              helperText={
                codeError ||
                "Invite code of the user new signups are attached to as their referrer"
              }
              fullWidth
            />
            <Box>
              <Typography
                variant="caption"
                color={tooLong ? "error" : "text.secondary"}
              >
                Link parameter: {startParamLength}/{MAX_START_PARAM} characters
                {tooLong ? " — use a shorter campaign name" : ""}
              </Typography>
              {previewLink && (
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, wordBreak: "break-all", fontFamily: "monospace" }}
                >
                  {previewLink}
                </Typography>
              )}
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {result ? (
          <>
            <Button onClick={reset}>Create another</Button>
            <Button variant="contained" onClick={handleClose}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
            >
              {submitting ? "Creating…" : "Create link"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
